import React, { useEffect, useState } from 'react';
import { translate } from 'components/Translation';
import CustomSearch, { ISearchElement } from 'components/CustomSearch/CustomSearch';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/topUpAddOns/changeCreditLimitDialogStyle';
import { makeStyles } from '@material-ui/core';
import { MIN_CREDIT_LIMIT, MAX_CREDIT_LIMIT } from 'utils/const/services';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { NumbersService } from 'services/numbers/numbers';
import { finalize } from 'rxjs/operators';
import { invalidateCache } from 'redux/modules/api/cache/actions';
import { setToast } from 'redux/modules/toast/actions';
import { useDispatch } from 'react-redux';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import OTP from 'components/OTP/OTP';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { UsersService } from 'services/users/users';
import OtpTab from 'views/Numbers/Details/Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);

const createElement = (value: string): ISearchElement => ({ id: value, value, content: value });

interface IChangeCreditLimitDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	phoneNumber: string;
	accountDetails: string;
	accountNumber: string;
	creditLimit: string;
	setCreditLimit: (value: string) => void;
	userData?: CorpUserData;
	documentId?: string;
	msisdn?: string;
}

const ChangeCreditLimitDialog = ({
	isOpen,
	hideDialog,
	phoneNumber,
	accountDetails,
	accountNumber,
	userData,
	creditLimit,
	setCreditLimit,
	documentId,
	msisdn = '',
}: IChangeCreditLimitDialogProps) => {
	const [newCreditLimit, setNewCreditLimit] = useState<ISearchElement | null>(null);
	const [showError, setShowError] = useState<boolean>(false);
	const [creditLimitChanging, setCreditLimitChanging] = useState(false);
	const classes = useStyles();
	const dispatch = useDispatch();
	const creditLimitNotValid: boolean = !newCreditLimit?.value;
	const [otpValue, setOtpValue] = useState<string>('');
	const [isOTP, setIsOtp] = useState<boolean>(false);
	const [isWrongOtp, setIsWrongOtp] = useState(false);

	const clearDialog = (): void => {
		setShowError(false);
		if (creditLimit) {
			setNewCreditLimit(createElement(creditLimit.toString()));
		} else {
			setNewCreditLimit(null);
		}
	};

	const close = (): void => {
		hideDialog();
		clearDialog();
		setIsOtp(false);
		setIsWrongOtp(false);
	};

	const onError = (): void => {
		setNewCreditLimit(createElement(creditLimit));
		setShowError(true);
		setIsOtp(false);
	};

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setIsWrongOtp(false);
		setOtpValue('');
		UsersService.requestForOtp(OperationContextEnum.CORPCHANGECREDITLIMIT, msisdn)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(() => {
				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
				setIsOtp(true);
			});
	};

	const changeCreditLimit = (): void => {
		dispatch(showLoadingIndicator());
		setCreditLimitChanging(true);
		NumbersService.changeCreditLimit({
			phoneNumber,
			accountNumber,
			documentId,
			limit: newCreditLimit?.value || '',
			existingLimit: creditLimit || '',
			accountId: userData?.accountId || '',
			otp: otpValue,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setCreditLimitChanging(false);
				})
			)
			.subscribe(
				({ response }) => {
					if (response.createProductOrderResponse.result.result.resultDescription === 'Success') {
						setCreditLimit(newCreditLimit?.value || '');
						invalidateCache(['getCreditLimit'], true);
						hideDialog();
						setIsOtp(false);
						setOtpValue('');
						setIsWrongOtp(false);
						dispatch(
							setToast({
								error: false,
								label: translate('numbers.setting.credit.limit.changed'),
								actionType: translate('changed'),
							})
						);
					} else {
						onError();
					}
				},
				(error) => {
					if (error.response.code === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
						return;
					}
					onError();
				}
			);
	};

	useEffect(() => {
		setNewCreditLimit(createElement(creditLimit));
	}, [creditLimit]);

	return (
		<CustomDialog
			isOpen={isOpen}
			loading={creditLimitChanging}
			hideDialog={close}
			header={translate('number.details.change.credit.limit')}
			dialogBody={
				<>
					<ConditionalRender
						show={!isOTP}
						onTrue={() => (
							<div className={classes.creditLimitBody}>
								<div className={classes.numberInfo}>
									<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} />
								</div>
								<div className={classes.creditLimitInput}>
									<CustomSearch
										id="creditLimit"
										label={translate('numbers.details.credit.limit')}
										elementSelected={(item: ISearchElement) => setNewCreditLimit(item)}
										elements={Array((MAX_CREDIT_LIMIT - MIN_CREDIT_LIMIT) / 5 + 1)
											.fill(0)
											.map((_: any, ind: number) => {
												const value = (ind * 5 + MIN_CREDIT_LIMIT).toString();
												return {
													id: value,
													value,
													content: value,
												};
											})}
										selectedElement={newCreditLimit}
									/>
								</div>
								<ConditionalRender
									show={showError}
									onTrue={() => (
										<div className={classes.errorBoxWrapper}>
											<ErrorBox
												text={
													<>
														{translate(
															'number.services.change.credit.limit.you.can.only.change.credit.limit'
														)}
														<br />
														<br />
														{translate(
															'number.services.change.credit.limit.change.after.monthly.limit.resets'
														)}
													</>
												}
											/>
										</div>
									)}
								/>
							</div>
						)}
					/>
					<ConditionalRender
						show={isOTP}
						onTrue={() => {
							return (
								<div className={classes.otpWrapper}>
									<OtpTab
										otp={otpValue}
										setOtp={setOtpValue}
										sendOtp={requestForOtp}
										otpIncorrect={isWrongOtp}
										error=""
										inOneInput={false}
										submitOTP={changeCreditLimit}
									/>
								</div>
							);
						}}
					/>
				</>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: close,
				},
				{
					text: translate('change'),
					color: 'primary-light',
					disabled: creditLimitNotValid || isOTP ? otpValue?.length < 5 : false,
					onClick: !isOTP ? requestForOtp : changeCreditLimit,
				},
			]}
		/>
	);
};

export default ChangeCreditLimitDialog;
